import axios from "axios";
import { apiUrl } from "./url";
import useAuthStore from "../../shared/store/useAuthStore";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const api = axios.create({
  baseURL: apiUrl,
  timeout: 200000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
});

// Request interceptor: add Bearer token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken;
    if (token && token !== "session_active") {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 and refresh token
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // Don't retry refresh endpoint or if already retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("refresh-token")
    ) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log("Attempting to refresh token...");
        const response = await api.post("v1/auth/refresh-token");

        const newToken = response.data?.token || response.data?.data?.token || response.data?.accessToken;
        
        if (newToken) {
          const user = useAuthStore.getState().user;
          useAuthStore.getState().login(user, newToken);
          
          // Process queued requests with new token
          processQueue(null, newToken);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          throw new Error("No token received");
        }
      } catch (refreshError) {
        // Process queue with error
        processQueue(refreshError, null);
        
        // If refresh token failed, clear auth
        if (refreshError.response?.status === 401) {
          console.warn("Refresh token expired or invalid. Clearing auth...");
          useAuthStore.getState().clearAuth();
          // Optional: redirect to login
          // window.location.href = "/SelectLoginPath";
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;